/**
 * Date/Time Utilities for Tappd
 * All times are stored in UTC in the database
 * All times are displayed in EST (America/New_York) to users
 */

// EST timezone identifier
export const TIMEZONE = 'America/New_York';

/**
 * Format a UTC date string to EST for display
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in EST
 */
export const formatDateEST = (dateInput, options = {}) => {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return '';
  
  const defaultOptions = {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  
  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format a UTC date string to EST date only (no time)
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {string} Formatted date string in EST (e.g., "Jan 9, 2026")
 */
export const formatDateOnlyEST = (dateInput) => {
  return formatDateEST(dateInput, {
    hour: undefined,
    minute: undefined,
    hour12: undefined,
  });
};

/**
 * Format a UTC date string to EST time only
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {string} Formatted time string in EST (e.g., "3:45 PM")
 */
export const formatTimeOnlyEST = (dateInput) => {
  return formatDateEST(dateInput, {
    year: undefined,
    month: undefined,
    day: undefined,
  });
};

/**
 * Format a UTC date to relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateInput) => {
  if (!dateInput) return 'Never';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return 'Invalid date';
  
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  
  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  
  // For older dates, show the actual date in EST
  return formatDateOnlyEST(date);
};

/**
 * Format a UTC date for display with "Last sent" style
 * Shows relative time for recent, full date for older
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {string} Formatted string
 */
export const formatLastSent = (dateInput) => {
  if (!dateInput) return 'Never sent';
  return formatRelativeTime(dateInput);
};

/**
 * Format a UTC date for invite expiry display
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {string} Formatted expiry string
 */
export const formatExpiry = (dateInput) => {
  if (!dateInput) return 'No expiry';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = new Date();
  
  if (date < now) {
    return 'Expired';
  }
  
  const diffMs = date - now;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffHours < 24) {
    return `Expires in ${diffHours}h`;
  }
  if (diffDays < 7) {
    return `Expires in ${diffDays}d`;
  }
  
  return `Expires ${formatDateOnlyEST(date)}`;
};

/**
 * Get current date/time in EST as a formatted string
 * @returns {string} Current date/time in EST
 */
export const getCurrentTimeEST = () => {
  return formatDateEST(new Date());
};

/**
 * Format a date for form inputs (YYYY-MM-DD)
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatForInput = (dateInput) => {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return '';
  
  // Convert to EST and format
  const estDate = new Date(date.toLocaleString('en-US', { timeZone: TIMEZONE }));
  
  const year = estDate.getFullYear();
  const month = String(estDate.getMonth() + 1).padStart(2, '0');
  const day = String(estDate.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};
